import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Modal,
  Button,
  ProgressBar,
  Badge,
  Card,
  ListGroup,
  Form,
  Tab,
  Tabs,
  Spinner,
  Alert,
  Dropdown,
} from "react-bootstrap";
import {
  FaTasks,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaFilter,
  FaSearch,
  FaPlus,
} from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import axios from "axios";

// API endpoints
const TASK_API_BASE = "http://localhost:5890";
const DEVELOPER_API_URL = "http://localhost:5890/adminperson/developers";

// Helper functions
const getBadgeVariant = (priority) => {
  switch (priority) {
    case "High":
      return "danger";
    case "Medium":
      return "warning";
    case "Low":
      return "success";
    default:
      return "secondary";
  }
};

const getStatusVariant = (status) => {
  switch (status) {
    case "Almost Done":
      return "success";
    case "In Progress":
      return "primary";
    case "Not Started":
      return "secondary";
    default:
      return "info";
  }
};

const getBorderColor = (priority) => {
  switch (priority) {
    case "High":
      return "border-start border-4 border-danger";
    case "Medium":
      return "border-start border-4 border-warning";
    case "Low":
      return "border-start border-4 border-success";
    default:
      return "";
  }
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case "High":
      return <FaExclamationTriangle className="text-danger me-1" />;
    case "Medium":
      return <FaArrowUp className="text-warning me-1" />;
    case "Low":
      return <FaArrowDown className="text-success me-1" />;
    default:
      return null;
  }
};

const TaskTracker = () => {
  const [tasks, setTasks] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [developerLoading, setDeveloperLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [newTask, setNewTask] = useState({
    task: "",
    priority: "Medium",
    assigned: "",
    developerId: "",
    dueDate: "",
    progress: 0,
    status: "Not Started",
    description: ""
  });
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);

  // Fetch tasks from API
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${TASK_API_BASE}/tasks`);
        // Ensure all tasks have the required fields and correct types
        const formattedTasks = response.data.map(task => ({
          ...task,
          task: task.task || "Untitled Task", // Default if task is missing
          assigned: task.assigned || "Unassigned",
          dueDate: task.dueDate || "No due date",
          progress: Number(task.progress) || 0,
          description: task.description || ""
        }));
        setTasks(formattedTasks);
      } catch (err) {
        setError(`Failed to fetch tasks: ${err.message}`);
        console.error("Error fetching tasks:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Fetch developers from API
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const token = sessionStorage.getItem("token");
        setDeveloperLoading(true);
        const response = await axios.get(DEVELOPER_API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setDevelopers(response.data);
      } catch (err) {
        setError(`Failed to fetch developers: ${err.message}`);
        console.error("Error fetching developers:", err);
      } finally {
        setDeveloperLoading(false);
      }
    };

    fetchDevelopers();
  }, []);

  const handleShowTaskModal = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setSelectedTask(null);
    setShowTaskModal(false);
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewTask({
      task: "",
      priority: "Medium",
      assigned: "",
      developerId: "",
      dueDate: "",
      progress: 0,
      status: "Not Started",
      description: ""
    });
    setSelectedDeveloper(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeveloperSelect = (developer) => {
    setSelectedDeveloper(developer);
    setNewTask(prev => ({
      ...prev,
      assigned: developer.developerName || developer.name || "Unknown",
      developerId: developer._id,
    }));
  };

  const handleAddTask = async () => {
    try {
      setLoading(true);
      const taskData = {
        employee_ID: newTask.developerId,
        task: newTask.task.toString(), // Ensure task is a string
        description: newTask.description.toString(),
        priority: newTask.priority,
        status: newTask.status,
        progress: parseInt(newTask.progress) || 0,
        dueDate: newTask.dueDate,
        assigned: newTask.assigned.toString(), // Ensure assigned is a string
      };

      const response = await axios.post(`${TASK_API_BASE}/add-task`, taskData);
      setTasks(prev => [...prev, {
        ...response.data,
        task: response.data.task || "Untitled Task",
        assigned: response.data.assigned || "Unassigned"
      }]);
      handleCloseAddModal();
    } catch (err) {
      setError(`Failed to add task: ${err.response?.data?.message || err.message}`);
      console.error("Error adding task:", err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      setLoading(true);
      const response = await axios.patch(`${TASK_API_BASE}/tasks/${taskId}`, { 
        status: newStatus 
      });
      setTasks(prev =>
        prev.map(task =>
          task.id === taskId ? { ...task, status: newStatus } : task
        )
      );
      setSelectedTask(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err) {
      setError(`Failed to update status: ${err.message}`);
      console.error("Error updating status:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const taskName = String(task.task || "").toLowerCase();
    const assignedName = String(task.assigned || "").toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();
    
    return taskName.includes(searchTermLower) || 
           assignedName.includes(searchTermLower);
  });

  // Filter tasks by status for tabs
  const notStartedTasks = filteredTasks.filter(task => task.status === "Not Started");
  const inProgressTasks = filteredTasks.filter(task => task.status === "In Progress");
  const almostDoneTasks = filteredTasks.filter(task => task.status === "Almost Done");

  return (
    <div className="container bg-white p-4 rounded-3 mt-4 shadow-sm border border-success">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-0 fw-bold text-success">
            <FaTasks className="me-2" />
            Task Tracker
          </h3>
          <p className="text-muted mb-0">Manage your team's tasks efficiently</p>
        </div>
        <div className="d-flex gap-2">
          <div className="input-group" style={{ width: "250px" }}>
            <span className="input-group-text bg-white">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline-success" className="d-flex align-items-center me-2">
            <FaFilter className="me-2" />
            Filter
          </Button>
          <Button variant="success" className="d-flex align-items-center" onClick={handleShowAddModal}>
            <FaPlus className="me-2" />
            Add Task
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
        fill
      >
        <Tab eventKey="all" title="All Tasks" className="border-0">
          {/* Content will be shown below */}
        </Tab>
        <Tab eventKey="notStarted" title="Not Started" className="border-0">
          {/* Content will be shown below */}
        </Tab>
        <Tab eventKey="inProgress" title="In Progress" className="border-0">
          {/* Content will be shown below */}
        </Tab>
        <Tab eventKey="almostDone" title="Almost Done" className="border-0">
          {/* Content will be shown below */}
        </Tab>
      </Tabs>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="success" />
          <p className="mt-2">Loading tasks...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover align-middle">
              <thead className="table-success">
                <tr>
                  <th>Task</th>
                  <th>Priority</th>
                  <th>Assigned</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === "all" ? filteredTasks :
                  activeTab === "notStarted" ? notStartedTasks :
                  activeTab === "inProgress" ? inProgressTasks :
                  almostDoneTasks).map((task) => (
                  <tr
                    key={task.id}
                    className={`${getBorderColor(task.priority)}`}
                    onClick={() => handleShowTaskModal(task)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="fw-semibold text-dark">{task.task || "Untitled Task"}</div>
                      <small className="text-muted">
                        {(task.description || "").substring(0, 50)}
                        {(task.description || "").length > 50 ? "..." : ""}
                      </small>
                    </td>
                    <td>
                      <Badge pill bg={getBadgeVariant(task.priority)} className="d-flex align-items-center">
                        {getPriorityIcon(task.priority)}
                        {task.priority || "Medium"}
                      </Badge>
                    </td>
                    <td className="d-flex align-items-center gap-2">
                      <div>
                        <div className="fw-medium">{task.assigned || "Unassigned"}</div>
                        <small className="text-muted">Developer</small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaCalendarAlt className="me-2 text-secondary" />
                        {task.dueDate || "No due date"}
                      </div>
                    </td>
                    <td>
                      <Badge bg={getStatusVariant(task.status)}>{task.status || "Not Started"}</Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <ProgressBar
                          now={task.progress || 0}
                          className="flex-grow-1 me-2"
                          variant={
                            (task.progress || 0) > 80 ? "success" : 
                            (task.progress || 0) > 50 ? "info" : "warning"
                          }
                        />
                        <span className="fw-medium">{task.progress || 0}%</span>
                      </div>
                    </td>
                    <td>
                      <Button variant="light" size="sm" className="rounded-circle">
                        <FiMoreVertical />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-md-none">
            {(activeTab === "all" ? filteredTasks :
              activeTab === "notStarted" ? notStartedTasks :
              activeTab === "inProgress" ? inProgressTasks :
              almostDoneTasks).map((task) => (
              <Card
                key={task.id}
                className={`mb-3 shadow-sm ${getBorderColor(task.priority)}`}
                onClick={() => handleShowTaskModal(task)}
                style={{ cursor: "pointer" }}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <Card.Title className="mb-1 text-success">{task.task || "Untitled Task"}</Card.Title>
                      <small className="text-muted">
                        {(task.description || "").substring(0, 60)}
                        {(task.description || "").length > 60 ? "..." : ""}
                      </small>
                    </div>
                    <Badge pill bg={getBadgeVariant(task.priority)}>
                      {task.priority || "Medium"}
                    </Badge>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div>
                      <div className="fw-medium">{task.assigned || "Unassigned"}</div>
                      <Badge bg={getStatusVariant(task.status)} size="sm">
                        {task.status || "Not Started"}
                      </Badge>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <FaCalendarAlt className="me-2 text-secondary" />
                      <small>Due: {task.dueDate || "No due date"}</small>
                    </div>
                    <div className="text-end">
                      <small className="fw-medium">{task.progress || 0}%</small>
                    </div>
                  </div>
                  <ProgressBar 
                    now={task.progress || 0}
                    variant={
                      (task.progress || 0) > 80 ? "success" : 
                      (task.progress || 0) > 50 ? "info" : "warning"
                    }
                  />
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Task Details Modal */}
      {selectedTask && (
        <Modal show={showTaskModal} onHide={handleCloseTaskModal} centered size="lg">
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold text-success">
              <FaTasks className="me-2" />
              Task Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h4 className="fw-bold mb-3">{selectedTask.task || "Untitled Task"}</h4>
            <div className="d-flex gap-3 mb-3">
              <Badge pill bg={getBadgeVariant(selectedTask.priority)}>{selectedTask.priority || "Medium"}</Badge>
              <Badge bg={getStatusVariant(selectedTask.status)}>{selectedTask.status || "Not Started"}</Badge>
            </div>
            <div className="row mb-4">
              <div className="col-md-6">
                <ListGroup variant="flush">
                  <ListGroup.Item className="border-0 px-0 py-2">
                    <strong>Assigned To:</strong> {selectedTask.assigned || "Unassigned"}
                  </ListGroup.Item>
                  <ListGroup.Item className="border-0 px-0 py-2">
                    <strong>Due Date:</strong> {selectedTask.dueDate || "No due date"}
                  </ListGroup.Item>
                </ListGroup>
              </div>
              <div className="col-md-6">
                <h6>Progress</h6>
                <ProgressBar
                  now={selectedTask.progress || 0}
                  variant={
                    (selectedTask.progress || 0) > 80 ? "success" :
                    (selectedTask.progress || 0) > 50 ? "info" : "warning"
                  }
                  className="mb-3"
                />
                <div className="d-flex gap-2">
                  <Button 
                    variant="success" 
                    size="sm"
                    onClick={() => handleUpdateStatus(selectedTask.id, "Almost Done")}
                    disabled={selectedTask.status === "Almost Done"}
                  >
                    <FaCheckCircle className="me-1" />
                    Mark as Complete
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => handleUpdateStatus(selectedTask.id, "In Progress")}
                    disabled={selectedTask.status === "In Progress"}
                  >
                    Start Progress
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <h6>Description</h6>
              <p className="text-muted">{selectedTask.description || "No description available"}</p>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Add Task Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success">
            <FaPlus className="me-2" />
            Add New Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Task Name</Form.Label>
              <Form.Control
                type="text"
                name="task"
                value={newTask.task}
                onChange={handleInputChange}
                placeholder="Enter task name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={newTask.description}
                onChange={handleInputChange}
                placeholder="Enter task description"
                required
              />
            </Form.Group>

            <div className="row mb-3">
              <Form.Group className="col-md-6">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  name="priority"
                  value={newTask.priority}
                  onChange={handleInputChange}
                  required
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={newTask.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Almost Done">Almost Done</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="row mb-3">
              <Form.Group className="col-md-6">
                <Form.Label>Assigned To</Form.Label>
                <Dropdown>
                  <Dropdown.Toggle variant="light" className="w-100 text-start">
                    {selectedDeveloper ? (selectedDeveloper.developerName || selectedDeveloper.name || "Unknown") : "Select Developer"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {developerLoading ? (
                      <Dropdown.Item>
                        <Spinner size="sm" animation="border" /> Loading developers...
                      </Dropdown.Item>
                    ) : (
                      developers.map(dev => (
                        <Dropdown.Item 
                          key={dev._id} 
                          onClick={() => handleDeveloperSelect(dev)}
                        >
                          <div className="d-flex align-items-center">
                            {dev.developerName || dev.name || "Unknown"}
                          </div>
                        </Dropdown.Item>
                      ))
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>

              <Form.Group className="col-md-6">
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  name="dueDate"
                  value={newTask.dueDate}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Progress ({newTask.progress}%)</Form.Label>
              <Form.Range
                name="progress"
                value={newTask.progress}
                onChange={handleInputChange}
                min="0"
                max="100"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={handleCloseAddModal}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleAddTask}
            disabled={!newTask.task || !newTask.developerId || !newTask.dueDate || !newTask.description}
          >
            {loading ? <Spinner size="sm" animation="border" /> : "Add Task"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaskTracker;